import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';

type Variant = 'lime' | 'glass' | 'danger' | 'ghost';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-2 font-medium rounded-xl transition-all duration-200 ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cubus-lime focus-visible:ring-offset-2 ' +
  'focus-visible:ring-offset-cubus-bg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';

const variants: Record<Variant, string> = {
  lime:
    'text-cubus-bg font-semibold border border-cubus-lime/80 ' +
    'bg-gradient-to-br from-[#E8FF30] to-[#C4E600] ' +
    'shadow-[0_8px_24px_rgba(221,255,0,0.25),inset_0_1px_0_rgba(255,255,255,0.4)] ' +
    'hover:shadow-[0_10px_32px_rgba(221,255,0,0.4),inset_0_1px_0_rgba(255,255,255,0.5)] ' +
    'hover:-translate-y-px active:translate-y-0',
  glass:
    'text-white/90 bg-white/[0.05] border border-white/10 backdrop-blur-sm ' +
    'hover:bg-white/[0.08] hover:border-white/20 hover:text-white',
  danger:
    'text-red-300 bg-red-500/10 border border-red-500/25 backdrop-blur-sm ' +
    'hover:bg-red-500/20 hover:border-red-500/40 hover:text-red-100',
  ghost:
    'text-white/70 bg-transparent border border-transparent ' +
    'hover:bg-white/[0.05] hover:text-white',
};

const sizes: Record<Size, string> = {
  sm: 'text-xs px-3 py-1.5 h-8',
  md: 'text-sm px-4 py-2 h-10',
  lg: 'text-base px-5 py-2.5 h-12',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'lime',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth,
    className = '',
    disabled,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`${base} ${variants[variant]} ${sizes[size]} ${fullWidth ? 'w-full' : ''} ${className}`}
      {...rest}
    >
      {loading ? (
        <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
      ) : (
        leftIcon
      )}
      {children}
      {!loading && rightIcon}
    </button>
  );
});
